// Astrea Ops Platform - keeping financial data away from non-finance logins (v7.63, 7 Oct 2026)
//
// Martin, 7 Oct 2026: "It is vital that no one other than Senior Management and Financial
// logins have access to any Financial data, ever."
//
// The database does the real work (supabase/migrations/20261007000001_finance_isolation.sql):
// projects.app_data, which every signed-in user can read, never holds money; the money lives
// in project_finance, which only Senior Management and Finance can read or write. This module
// is the app's side of the same rule:
//   - mergeFinance(entry, fin) puts a project back together for a finance login.
//   - stripFinance(entry) removes every financial part from a project object. It runs on
//     every project loaded for anyone else (defence in depth - the database has already
//     removed it) and for a System Admin emulating a non-finance role.
//   - isPublicProposalKey(k) is the allow-list of calculator keys everyone may see. It must
//     match finance_public_proposal_key() in the migration; scripts/security/split.test.js
//     checks the two against each other.
// Plain functions with no imports, so the tests can load this file directly.

export const FIN_PUBLIC_PROPOSAL_KEYS = [
  'rmCount', 'attendeeSize', 'managementVisit', 'medicalDays', 'rateYear', 'daysFromPositions',
  'pomName', 'adminName', 'writerName', 'financeName', 'documentWriter', 'documentWriterId',
  'documentReviewer', 'status', 'sowSigned', 'sowSignedDate', 'sowSentDate',
];
const PUBLIC_SET = new Set(FIN_PUBLIC_PROPOSAL_KEYS);

// Days, who is on a row and which visit a row belongs to are not money; anything to do with
// flights, company-card travel or unpaid days is.
export function isPublicProposalKey(k) {
  if (PUBLIC_SET.has(k)) return true;
  return /(Days|UserId|Visit)$/.test(k) && !/(flight|cadence|spotnana|unpaid)/i.test(k);
}

export const FIN_WHOLE_KEYS = ['financials', 'financialTracking', 'estimatedPrice'];
export const FIN_PROSPECT_KEYS = ['proposedValue', 'wonValue', 'estimatedBudget'];
export const FIN_FILE_CATEGORIES = ['SOW', 'Contract'];

export const isFinancialLogEntry = e => !!e && e.type === 'financial';
export const isFinancialNote = n => !!n && n.type === 'financial_note';
export const isFinancialFile = f => !!f && FIN_FILE_CATEGORIES.includes(f.category);

export function publicProposal(p) {
  if (!p || typeof p !== 'object') return p;
  const out = {};
  Object.keys(p).forEach(k => { if (isPublicProposalKey(k)) out[k] = p[k]; });
  return out;
}

// Merges two lists of objects by id; items only in `extra` are added.
function unionById(base, extra) {
  const list = Array.isArray(base) ? [...base] : [];
  const ids = new Set(list.map(x => x && x.id).filter(Boolean));
  (Array.isArray(extra) ? extra : []).forEach(x => { if (!x || !x.id || !ids.has(x.id)) list.push(x); });
  return list;
}
const byTime = (a, b) => String(a?.timestamp || '').localeCompare(String(b?.timestamp || ''));

// A project with every financial part removed. Never mutates its argument.
export function stripFinance(entry) {
  if (!entry || typeof entry !== 'object') return entry;
  const out = { ...entry };
  delete out._finLoaded;
  if (out.proposal && typeof out.proposal === 'object') out.proposal = publicProposal(out.proposal);
  FIN_WHOLE_KEYS.forEach(k => { delete out[k]; });
  if (out.prospectData && typeof out.prospectData === 'object') {
    const pd = { ...out.prospectData };
    FIN_PROSPECT_KEYS.forEach(k => { delete pd[k]; });
    out.prospectData = pd;
  }
  if (Array.isArray(out.activityLog)) out.activityLog = out.activityLog.filter(e => !isFinancialLogEntry(e));
  if (Array.isArray(out.progressNotes)) out.progressNotes = out.progressNotes.filter(n => !isFinancialNote(n));
  if (Array.isArray(out.files)) out.files = out.files.filter(f => !isFinancialFile(f));
  return out;
}

// Puts a project back together for a finance login from its app_data part (entry) and its
// project_finance row (fin, may be null). The calculator's public keys always come from
// app_data, because anyone on the team can change them there (writer, POM, days).
export function mergeFinance(entry, fin) {
  const out = { ...entry };
  const f = fin && typeof fin === 'object' ? fin : {};
  const pub = out.proposal && typeof out.proposal === 'object' ? out.proposal : null;
  // app_data's own proposal is the base: after the migration it holds only public keys, but
  // before it (or on a project never split) it still holds everything, which must be kept.
  if (pub || f.proposalPrivate) out.proposal = { ...(pub || {}), ...(f.proposalPrivate || {}), ...(pub ? publicProposal(pub) : {}) };
  if (f.financials !== undefined) out.financials = f.financials;
  out.financialTracking = f.financialTracking || out.financialTracking || {};
  if (f.estimatedPrice !== undefined) out.estimatedPrice = f.estimatedPrice;
  if (f.prospectPrivate && out.prospectData && typeof out.prospectData === 'object') out.prospectData = { ...out.prospectData, ...f.prospectPrivate };
  if (Array.isArray(f.activity) && f.activity.length) out.activityLog = unionById(out.activityLog, f.activity).sort(byTime);
  if (Array.isArray(f.notes) && f.notes.length) out.progressNotes = unionById(out.progressNotes, f.notes).sort(byTime);
  if (Array.isArray(f.files) && f.files.length) out.files = unionById(out.files, f.files);
  return out;
}

// Re-applies app_data's public calculator keys on top of a proposal that the proposals table
// has since been merged into (the table only Finance can write may be older for those keys).
export function reapplyPublicProposal(proposal, appDataProposal) {
  if (!proposal || !appDataProposal) return proposal;
  return { ...proposal, ...publicProposal(appDataProposal) };
}
